
import { playEarconOscillators } from './earcons';
import type { AudioSink } from './engine/AudioSink';

/**
 * Wrapper around the HTML5 Audio element to handle playback of Blobs and URLs.
 * Provides a consistent interface for controlling playback, volume, and rate,
 * and abstracts away the URL.createObjectURL/revokeObjectURL lifecycle.
 *
 * This is the production main-thread implementation of {@link AudioSink}.
 */
export class AudioElementPlayer implements AudioSink {
  private audio: HTMLAudioElement;
  private onTimeUpdateCallback: ((time: number) => void) | null = null;
  private onEndedCallback: (() => void) | null = null;
  private onErrorCallback: ((error: MediaError | null) => void) | null = null;
  private currentObjectUrl: string | null = null;
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private sourceNode: MediaElementAudioSourceNode | null = null;

  /**
   * Initializes a new instance of AudioElementPlayer.
   * Creates the underlying Audio element and attaches event listeners.
   */
  constructor() {
    this.audio = new Audio();
    this.attachListeners();
  }

  /**
   * Attaches internal event listeners to the Audio element to propagate events.
   */
  private attachListeners() {
    this.audio.ontimeupdate = () => {
      if (this.onTimeUpdateCallback) {
        this.onTimeUpdateCallback(this.audio.currentTime);
      }
    };

    this.audio.onended = () => {
      this.revokeCurrentUrl();
      if (this.onEndedCallback) {
        this.onEndedCallback();
      }
    };

    this.audio.onerror = () => {
      // The failed segment is done with its object URL, exactly like the ended
      // one above. Every terminal path (playBlob/playUrl/stop/onended/onerror)
      // revokes it: after a fallback swap (the engine's recovery moves to a
      // local provider that never touches this sink) no later playBlob comes to
      // revoke it, so a run of failures would otherwise strand one blob each.
      // The revoke runs FIRST and cannot break the error path:
      // revokeCurrentUrl never throws, and the MediaError is read before it so
      // the callback receives it unchanged.
      const error = this.audio.error;
      this.revokeCurrentUrl();
      if (this.onErrorCallback) {
        this.onErrorCallback(error);
      }
    };
  }

  /**
   * Revokes the current Object URL to prevent memory leaks.
   *
   * Best-effort and non-throwing: every caller is on a terminal path (play swap,
   * stop, ended, error) where a failed revoke must not abort the work that
   * follows it. The handle is dropped BEFORE the revoke, so a throw can never
   * leave it behind to be revoked a second time.
   */
  private revokeCurrentUrl() {
      const url = this.currentObjectUrl;
      if (!url) return;
      this.currentObjectUrl = null;
      try {
          URL.revokeObjectURL(url);
      } catch { /* the handle is gone either way */ }
  }

  /**
   * Plays audio from a Blob object.
   *
   * @param blob - The audio Blob to play.
   * @returns A Promise that resolves when playback begins.
   */
  public playBlob(blob: Blob): Promise<void> {
    this.revokeCurrentUrl();
    const url = URL.createObjectURL(blob);
    this.currentObjectUrl = url;
    this.audio.src = url;
    return this.audio.play();
  }

  /**
   * Plays audio from a URL string.
   *
   * @param url - The URL of the audio to play.
   * @returns A Promise that resolves when playback begins.
   */
  public playUrl(url: string): Promise<void> {
    this.revokeCurrentUrl();
    this.audio.src = url;
    return this.audio.play();
  }

  /**
   * Pauses playback.
   */
  public pause() {
    this.audio.pause();
  }

  /**
   * Resumes playback.
   *
   * @returns A Promise that resolves when playback resumes.
   */
  public resume(): Promise<void> {
    return this.audio.play();
  }

  /**
   * Stops playback, resets the position to the beginning, and cleans up resources.
   */
  public stop() {
    this.audio.pause();
    this.audio.currentTime = 0;
    this.revokeCurrentUrl();
  }

  /**
   * Sets the playback volume.
   *
   * @param volume - The volume level (0.0 to 1.0).
   */
  public setVolume(volume: number) {
    this.audio.volume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Sets the playback rate.
   *
   * Pins both `defaultPlaybackRate` and `playbackRate`: the media load algorithm
   * resets `playbackRate` to `defaultPlaybackRate` whenever a new source is loaded,
   * so setting both makes the rate survive subsequent `src` assignments. Rate is a
   * playback-time-only parameter — audio is always synthesized at 1.0 and sped
   * up/slowed down here at the sink.
   *
   * @param rate - The playback speed (e.g., 1.0 for normal speed).
   */
  public setRate(rate: number) {
    this.audio.defaultPlaybackRate = rate;
    this.audio.playbackRate = rate;
  }

  /**
   * Seeks to a specific time in the audio.
   *
   * @param time - The time in seconds to seek to.
   */
  public seek(time: number) {
    if (isFinite(time)) {
       this.audio.currentTime = Math.max(0, Math.min(time, this.audio.duration || 0));
    }
  }

  /**
   * Gets the current playback time.
   *
   * @returns The current time in seconds.
   */
  public getCurrentTime(): number {
    return this.audio.currentTime;
  }

  /**
   * Gets the duration of the audio.
   *
   * @returns The duration in seconds.
   */
  public getDuration(): number {
    return this.audio.duration;
  }

  /**
   * Sets the callback for time update events.
   *
   * @param callback - The function to call when playback time updates.
   */
  public setOnTimeUpdate(callback: (time: number) => void) {
    this.onTimeUpdateCallback = callback;
  }

  /**
   * Sets the callback for playback ended events.
   *
   * @param callback - The function to call when playback ends.
   */
  public setOnEnded(callback: () => void) {
    this.onEndedCallback = callback;
  }

  /**
   * Sets the callback for error events.
   *
   * @param callback - The function to call when an error occurs.
   */
  public setOnError(callback: (error: MediaError | null) => void) {
    this.onErrorCallback = callback;
  }

  /**
   * Ensures Web Audio API context is initialized for ducking.
   */
  private ensureAudioContext() {
    if (!this.audioContext) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        this.audioContext = new Ctx();
        this.gainNode = this.audioContext.createGain();
        this.sourceNode = this.audioContext.createMediaElementSource(this.audio);
        this.sourceNode.connect(this.gainNode);
        this.gainNode.connect(this.audioContext.destination);
      }
    }
    if (this.audioContext && this.audioContext.state === 'suspended') {
      // resume() rejects on a closed context; ducking is best-effort.
      void Promise.resolve(this.audioContext.resume()).catch(() => { /* best-effort */ });
    }
  }

  /**
   * Plays an earcon, automatically ducking the main audio if playing.
   */
  public playEarcon(type: 'bookmark_captured' | 'bookmark_failed') {
    this.ensureAudioContext();
    if (!this.audioContext || !this.gainNode) return;

    const now = this.audioContext.currentTime;

    // 1. Duck the main TTS volume
    this.gainNode.gain.cancelScheduledValues(now);
    this.gainNode.gain.setValueAtTime(this.gainNode.gain.value, now);
    this.gainNode.gain.linearRampToValueAtTime(0.3, now + 0.1);
    this.gainNode.gain.linearRampToValueAtTime(1.0, now + 0.6);

    // 2. Play earcon using Oscillator
    playEarconOscillators(this.audioContext, type, this.audioContext.destination);
  }

  /**
   * Destroys the player instance and clears all listeners.
   */
  public destroy() {
      this.stop();
      this.audio.ontimeupdate = null;
      this.audio.onended = null;
      this.audio.onerror = null;
      if (this.sourceNode) {
          this.sourceNode.disconnect();
      }
      if (this.gainNode) {
          this.gainNode.disconnect();
      }
      if (this.audioContext) {
          // close() rejects if the context is already closed (e.g. a second destroy()).
          void Promise.resolve(this.audioContext.close()).catch(() => { /* already closed */ });
      }
  }
}
